/**
 * Input Form - Alle Eingabefelder für die Kalkulation
 */

import type { CalculatorInput } from '@/types/price-calculator';
import { TAX_PRESETS } from '@/lib/price-calculator';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface InputFormProps {
  input: CalculatorInput;
  onUpdate: <K extends keyof CalculatorInput>(field: K, value: CalculatorInput[K]) => void;
}

function NumberInput({
  label,
  value,
  onChange,
  suffix = '€',
  step = 0.01,
  min = 0,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  step?: number;
  min?: number;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{label}</label>
      <div className="relative">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          step={step}
          min={min}
          className={cn(
            'w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600',
            'bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100',
            'focus:ring-2 focus:ring-primary-500 focus:border-transparent',
            'text-right pr-8'
          )}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 text-sm">
          {suffix}
        </span>
      </div>
    </div>
  );
}

export function InputForm({ input, onUpdate }: InputFormProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      {/* Basis */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
          {t('pricing.basis')}
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label={t('pricing.purchasePriceNet')}
            value={input.purchasePrice}
            onChange={(v) => onUpdate('purchasePrice', v)}
          />
          <NumberInput
            label={t('pricing.sellingPriceGross')}
            value={input.sellingPrice}
            onChange={(v) => onUpdate('sellingPrice', v)}
          />
        </div>
      </div>

      {/* Steuersatz */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
          {t('pricing.taxRate')}
        </h3>
        <div className="flex flex-wrap gap-2">
          {TAX_PRESETS.map((tax) => (
            <button
              key={tax.id}
              onClick={() => onUpdate('taxRate', tax.rate)}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                input.taxRate === tax.rate
                  ? 'bg-primary-600 text-white'
                  : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
              )}
            >
              {tax.label}
            </button>
          ))}
        </div>
      </div>

      {/* Versand */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
          {t('pricing.shipping')}
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label={t('pricing.shippingCostSelf')}
            value={input.shippingCost}
            onChange={(v) => onUpdate('shippingCost', v)}
          />
          <NumberInput
            label={t('pricing.chargedToCustomer')}
            value={input.shippingToCustomer}
            onChange={(v) => onUpdate('shippingToCustomer', v)}
          />
        </div>
      </div>

      {/* Sonstige Kosten */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
          {t('pricing.otherCostsSection')}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <NumberInput
            label={t('pricing.packagingLabel')}
            value={input.packagingCost}
            onChange={(v) => onUpdate('packagingCost', v)}
          />
          <NumberInput
            label={t('pricing.returnRate')}
            value={input.returnRate}
            onChange={(v) => onUpdate('returnRate', v)}
            suffix="%"
          />
          <NumberInput
            label={t('pricing.returnCostPer')}
            value={input.returnCost}
            onChange={(v) => onUpdate('returnCost', v)}
          />
          <NumberInput
            label={t('pricing.otherLabel')}
            value={input.otherCosts}
            onChange={(v) => onUpdate('otherCosts', v)}
          />
        </div>
      </div>
    </div>
  );
}
